import React, { createContext, useContext, useReducer, Dispatch, useEffect } from "react";
import { useThemeStore } from "./ThemeStore";

export enum LoadingSpinnerType {
    Casablanca = "casablanca",
    Grid = "grid",
    Watch = "watch",
    Rings = "rings",
}

export interface IConfigurationState {
    setup: {
        tenantId: string;
        deviceName: string;
        placeholderUrl: string;
    },
    design: {
        companyLogoUrl: string;
        companyName: string;
        showLogo: boolean;
        loadingSpinnerType: LoadingSpinnerType;
        companyHexColor: string;
        openLinksInModal: boolean
    }
}

export const initialState: IConfigurationState = {
    setup: {
        tenantId: "",
        deviceName: "",
        placeholderUrl: ""
    },
    design: {
        companyLogoUrl: "",
        companyName: "E-Display",
        showLogo: false,
        loadingSpinnerType: LoadingSpinnerType.Grid,
        companyHexColor: "#1976d2",
        openLinksInModal: false
    }
};

type ConfigurationActions =
    | { type: 'SET_TENANT_ID'; payload: string }
    | { type: 'SET_DEVICE_NAME'; payload: string }
    | { type: 'SET_PLACEHOLDER_URL'; payload: string }
    | { type: 'SET_COMPANY_LOGO_URL'; payload: string }
    | { type: 'SET_COMPANY_NAME'; payload: string }
    | { type: 'SET_SHOW_LOGO'; payload: boolean }
    | { type: 'SET_LOADING_SPINNER_TYPE'; payload: LoadingSpinnerType }
    | { type: 'SET_COMPANY_HEX_COLOR'; payload: string }
    | { type: 'SET_OPEN_LINKS_IN_MODAL'; payload: boolean }
    | { type: 'CLEAR_CONFIGS' };


interface IConfigurationContext {
    configuration: IConfigurationState
    dispatchConfiguration: Dispatch<ConfigurationActions>;
}
const ConfigurationsReducer = (state: IConfigurationState, action: ConfigurationActions): IConfigurationState => {
    switch (action.type) {
        case 'SET_TENANT_ID':
            return {
                ...state,
                setup: {
                    ...state.setup,
                    tenantId: action.payload
                }
            };
        case 'SET_DEVICE_NAME':
            return {
                ...state,
                setup: {
                    ...state.setup,
                    deviceName: action.payload
                }
            };
        case 'SET_PLACEHOLDER_URL':
            if (!action.payload.includes('display.casablanca')) {
                return {
                    ...state,
                    setup: {
                        ...state.setup,
                        placeholderUrl: action.payload
                    }
                };
            }
        case 'SET_COMPANY_LOGO_URL':
            return {
                ...state,
                design: {
                    ...state.design,
                    companyLogoUrl: action.payload
                }
            };
        case 'SET_COMPANY_NAME':
            return {
                ...state,
                design: {
                    ...state.design,
                    companyName: action.payload
                }
            };
        case 'SET_SHOW_LOGO':
            return {
                ...state,
                design: {
                    ...state.design,
                    showLogo: action.payload
                }
            };
        case 'SET_LOADING_SPINNER_TYPE':
            return {
                ...state,
                design: {
                    ...state.design,
                    loadingSpinnerType: action.payload
                }
            };
        case 'SET_COMPANY_HEX_COLOR':
            return {
                ...state,
                design: {
                    ...state.design,
                    companyHexColor: action.payload
                }
            };
        case 'SET_OPEN_LINKS_IN_MODAL':
            return {
                ...state,
                design: {
                    ...state.design,
                    openLinksInModal: action.payload
                }
            };
        case 'CLEAR_CONFIGS':
            return { ...initialState };
        default:
            console.warn("Unknown action type");
            return state;
    }
};
const ConfigurationContext = createContext<IConfigurationContext>({
    configuration: initialState,
    dispatchConfiguration: () => { },
});
export const ConfigurationsProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    const { setPrimaryColor } = useThemeStore()
    const loadState = () => {
        try {
            const serializedState = localStorage.getItem('configuration');
            if (serializedState === null) {
                return initialState;
            }
            const storedState = JSON.parse(serializedState)
            return storedState;
        } catch (err) {
            return initialState;
        }
    };

    const [configuration, dispatchConfiguration] = useReducer(ConfigurationsReducer, loadState());

    useEffect(() => {
        if (configuration.design.companyHexColor) {
            setPrimaryColor(configuration.design.companyHexColor);
        }
    }, [configuration.design.companyHexColor, setPrimaryColor]);

    useEffect(() => {
        try {
            const serializedState = JSON.stringify(configuration);
            localStorage.setItem('configuration', serializedState);
        } catch (err) {
            console.error("Error saving data to local storage", err);
        }
    }, [configuration]);
    const contextValue = {
        configuration,
        dispatchConfiguration
    };

    return (
        <ConfigurationContext.Provider value={contextValue}>
            {children}
        </ConfigurationContext.Provider>
    );
};

export const useConfigurationStore = (): IConfigurationContext => {
    const context = useContext(ConfigurationContext);
    if (context === undefined) {
        throw new Error("useConfigurationsStore must be used within a ConfigurationsProvider");
    }
    return context;
};
